// SignatureFunc.js - Clean version with multiple signatures only

// findProposalId helper function
const findProposalId = (projectObj) => {
  if (!projectObj) return null;

  const possibleKeys = [  
    'acquisition_proposal_no',
    'land_acquisition_no',
    'proposal_id',
    'proposalId',
    'proposal',
    'id',
    'project_id'
  ];

  for (const key of possibleKeys) {
    if (projectObj[key] !== undefined && projectObj[key] !== null && projectObj[key] !== '') {
      return projectObj[key];
    }
  }

  return null;
};

// Digital signature dialog for multiple signatures
const showDigitalSignatureDialog = () => {
  return new Promise((resolve) => {
    const modal = document.createElement("div");
    modal.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.5); display: flex; align-items: center;
      justify-content: center; z-index: 1000;
    `;

    const dialog = document.createElement("div");
    dialog.style.cssText = `
      background: white; padding: 20px; border-radius: 8px;
      max-width: 400px; width: 90%;
    `;

    dialog.innerHTML = `
      <h3 style="margin-top: 0;">Digital Signature Parameters</h3>
      <form id="signatureForm">
        <div style="margin-bottom: 15px;">
          <label style="display:block; margin-bottom:5px;">Token Name:</label>
          <select id="tokenName" required 
            style="width:100%; padding:8px; border:1px solid #ccc; border-radius:4px;">
            <option value="">Select Token</option>
            <option value="ProxKey">ProxKey</option>
            <option value="ePass2003 Auto">ePass2003 Auto</option>
            <option value="Moser Baer">Moser Baer</option>
            <option value="Safenet/Gemalto">Safenet/Gemalto</option>
          </select>
        </div>

        <div style="margin-bottom: 15px;">
          <label style="display:block; margin-bottom:5px;">PIN:</label>
          <input type="password" id="pin" required 
            style="width:100%; padding:8px; border:1px solid #ccc; border-radius:4px;">
        </div>

        <div style="margin-bottom: 20px;">
          <label style="display:block; margin-bottom:5px;">Place Text (Optional):</label>
          <input type="text" id="placeText" placeholder="e.g., Mumbai, India"
            style="width:100%; padding:8px; border:1px solid #ccc; border-radius:4px;">
        </div>

        <div style="display:flex; gap:10px; justify-content:flex-end;">
          <button type="button" id="cancelBtn" 
            style="padding:8px 16px; border:1px solid #ccc; background:white; border-radius:4px; cursor:pointer;">
            Cancel
          </button>
          <button type="submit" 
            style="padding:8px 16px; border:none; background:#007bff; color:white; border-radius:4px; cursor:pointer;">
            Sign PDF
          </button>
        </div>
      </form>
    `;

    modal.appendChild(dialog);
    document.body.appendChild(modal);

    const form = dialog.querySelector("#signatureForm");
    const cancelBtn = dialog.querySelector("#cancelBtn");

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const params = {
        tokenName: dialog.querySelector("#tokenName").value,
        pin: dialog.querySelector("#pin").value,
        placeText: dialog.querySelector("#placeText").value,
      };

      document.body.removeChild(modal);
      resolve(params);
    });

    cancelBtn.addEventListener("click", () => {
      document.body.removeChild(modal);
      resolve(null);
    });

    modal.addEventListener("click", (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
        resolve(null);
      }
    });
  });
};

// Main function for multiple signatures
export const handleMultipleSignatures = async (file, signaturesArray, { setUploadStatus, setUploadError, setSelectedSignDocument }) => {
  try {
    setUploadStatus("uploading");
    setUploadError("");

    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    const projectIdFromStorage = selectedProject?.id || selectedProject?.project_id;
    const proposalId = localStorage.getItem('currentProposalId') || findProposalId(selectedProject);

    if (!projectIdFromStorage || !proposalId) {
      setUploadStatus("error");
      setUploadError("Missing projectId or proposalId");
      alert("Missing projectId or proposalId");
      return;
    }

    const BRIDGE_URL = "http://127.0.0.1:8000";

    // Get signature parameters
    const signatureParams = await showDigitalSignatureDialog();
    if (!signatureParams) {
      setUploadStatus("idle");
      return;
    }

    // Health check
    try {
      const healthCheck = await fetch(`${BRIDGE_URL}/health`, { method: "GET" });
      if (!healthCheck.ok) {
        throw new Error("Bridge app not accessible");
      }
    } catch (error) {
      setUploadStatus("error");
      setUploadError("Digital signature bridge app is not running. Please start it first.");
      alert("Digital signature bridge app is not running. Please start it first.");
      return;
    }

    // Prepare form data for multiple signatures
    const formData = new FormData();
    formData.append("pdf_file", file);
    formData.append("token_name", signatureParams.tokenName);
    formData.append("pin", signatureParams.pin);
    formData.append("place_text", signatureParams.placeText || "");
    formData.append("signatures_json", JSON.stringify(signaturesArray));

    // Use the multiple signature endpoint
    const bridgeResponse = await fetch(`${BRIDGE_URL}/sign-pdf-multiple/`, {
      method: "POST",
      body: formData,
    });

    if (!bridgeResponse.ok) {
      const errorData = await bridgeResponse.json().catch(() => ({ detail: "Unknown error" }));
      throw new Error(errorData.detail || `Bridge app error: ${bridgeResponse.status}`);
    }

    const signedPdfBlob = await bridgeResponse.blob();
    const signedPdfFile = new File([signedPdfBlob], `signed_${file.name}`, { type: "application/pdf" });

    setSelectedSignDocument(signedPdfFile);
    setUploadStatus("success");
    alert(`PDF digitally signed with ${signaturesArray.length} signature(s) successfully!`);

  } catch (error) {
    setUploadStatus("error");
    setUploadError(error.message);
    console.error("Digital signature error:", error);

    if (error.message.includes("PIN is incorrect")) {
      alert("The provided PIN is incorrect. Please try again.");
    } else if (error.message.includes("token found")) {
      alert("No USB token found. Please connect your digital signature device.");
    } else if (error.message.includes("Bridge app not running")) {
      alert("Digital signature bridge app is not running. Please start it first.");
    } else {
      alert(`Error during digital signing: ${error.message}`);
    }
  }
};

// ===================================================================
// useSignature.js - Clean version with multiple signatures only

// import { useState } from 'react';
// import { handleMultipleSignatures } from './SignatureFunc.js';

// export const useSignature = () => {
//     const [uploadStatus, setUploadStatus] = useState("idle");
//     const [uploadError, setUploadError] = useState("");
//     const [selectedSignDocument, setSelectedSignDocument] = useState(null);

//     // Multiple signature method
//     const signDocument = async (file, signaturesArray) => {
//         await handleMultipleSignatures(file, signaturesArray, { setUploadStatus, setUploadError, setSelectedSignDocument });
//     };

//     const resetSignatureState = () => {
//         setUploadStatus("idle");
//         setUploadError("");
//         setSelectedSignDocument(null);
//     };

//     return {
//         uploadStatus,
//         uploadError,
//         selectedSignDocument,
//         signDocument,
//         resetSignatureState,
//     };
// };

// ===================================================================
// Usage Example in Component:

/*
import React, { useState } from 'react';
import SignaturePad from './SignaturePad';
import { useSignature } from './useSignature';

const DocumentSigner = () => {
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const [pdfForSigning, setPdfForSigning] = useState(null);
    
    const { signDocument, uploadStatus, uploadError, selectedSignDocument } = useSignature();

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file && file.type === 'application/pdf') {
            setPdfForSigning(file);
            setShowSignaturePad(true);
        }
    };

    const handleSignatures = async (signaturesArray) => {
        setShowSignaturePad(false);
        if (pdfForSigning) {
            await signDocument(pdfForSigning, signaturesArray);
            setPdfForSigning(null);
        }
    };

    const handleCancel = () => {
        setShowSignaturePad(false);
        setPdfForSigning(null);
    };

    return (
        <div>
            <input type="file" accept=".pdf" onChange={handleFileSelect} />
            
            {showSignaturePad && pdfForSigning && (
                <SignaturePad 
                    pdfFile={pdfForSigning}
                    onSign={handleSignatures}
                    onCancel={handleCancel}
                />
            )}
            
            {uploadStatus === 'success' && selectedSignDocument && (
                <a href={URL.createObjectURL(selectedSignDocument)} download={selectedSignDocument.name}>
                    Download Signed PDF
                </a>
            )}
        </div>
    );
};
*/